/**
 * Shared state between the calendar panel and the details form.
 * Replaces the old `useSharedData` pub/sub pattern with a simpler Zustand-like store.
 *
 * Both the calendar and the form read from this state.
 * Updates are explicit — no implicit pub/sub cascades.
 */
import { useCallback, useRef, useSyncExternalStore } from 'react';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export interface ScheduleSlot {
  start: string;
  end: string;
  duration: number;
}

export interface ScheduleSharedState {
  // Interview metadata
  interviewName: string;
  /**
   * Interview name as it was hydrated from the API on edit mount.
   * Used by the calendar to dedupe the stale busy-events copy of THIS
   * interview: the backend's /calendar_events cache can keep an old
   * entry after a time/duration edit, so we hide any cached event
   * whose subject still matches the original name. Empty on create.
   */
  originalInterviewName: string;
  interviewKind: 'one_on_one' | 'panel';
  interviewerIds: number[];
  interviewerDetails: Array<{
    id: number;
    name: string;
    email?: string;
    avatar?: string;
    timezone?: string;
  }>;

  // Scheduling (manual mode — single slot)
  bookedSlot: ScheduleSlot | null;
  /**
   * Current interview duration (minutes). Lives at the top level so
   * the form can update it before any slot is booked — otherwise the
   * value would be trapped inside `bookedSlot`, which stays null until
   * the user actually picks a time.
   */
  duration: number;

  // Scheduling (self-schedule mode — multiple slots)
  selfScheduleSlots: ScheduleSlot[];

  // Calendar navigation
  calendarWeekStart: string;
  timezone: string;
  /**
   * Interviewer IDs whose Google/Outlook calendar isn't connected —
   * the busy-events API flags them via `calendar_available: false`.
   * The Details form uses this to render a warning icon on each
   * selected-interviewer chip.
   */
  calendarDisconnectedInterviewerIds: number[];

  // Meeting infrastructure
  meetingRoomIds: number[];
  /**
   * Canonical store for selected meeting-room identifiers — rooms are
   * keyed by *email* on the backend, not numeric id. The form field
   * still ships an array of emails here.
   */
  meetingRoomEmails: string[];
  /** `email → display name` so the calendar can show a room's friendly
   *  label on a busy event instead of the raw meeting subject. */
  meetingRoomNamesByEmail: Record<string, string>;
  location: string;
  hostId: number | null;

  // Toggles
  calendarVisibility: 'default' | 'private';
  noteTakerEnabled: boolean;
  fillWithAiEnabled: boolean;

  /**
   * Self-schedule buffer (minutes) applied around generated slots.
   * `bufferEnabled=false` makes the pipeline + payload ignore these
   * numbers — matches the eye-toggle on the form field. Hidden behind
   * `feInterviewBuffer`; when the flag is off the calendar + form never
   * read these values.
   */
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  bufferEnabled: boolean;
}

const DEFAULT_STATE: ScheduleSharedState = {
  interviewName: '',
  originalInterviewName: '',
  interviewKind: 'one_on_one',
  interviewerIds: [],
  interviewerDetails: [],
  bookedSlot: null,
  duration: 30,
  selfScheduleSlots: [],
  calendarWeekStart: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  calendarDisconnectedInterviewerIds: [],
  meetingRoomIds: [],
  meetingRoomEmails: [],
  meetingRoomNamesByEmail: {},
  location: 'zoom',
  hostId: null,
  calendarVisibility: 'default',
  noteTakerEnabled: false,
  fillWithAiEnabled: true,
  bufferBeforeMinutes: 15,
  bufferAfterMinutes: 15,
  bufferEnabled: true,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

type Listener = () => void;

function createScheduleStore(initial: Partial<ScheduleSharedState> = {}) {
  let state: ScheduleSharedState = { ...DEFAULT_STATE, ...initial };
  const listeners = new Set<Listener>();

  return {
    getState: () => state,
    setState: (partial: Partial<ScheduleSharedState>) => {
      state = { ...state, ...partial };
      listeners.forEach((l) => l());
    },
    subscribe: (listener: Listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useScheduleSharedState(initial?: Partial<ScheduleSharedState>) {
  const storeRef = useRef(createScheduleStore(initial));
  const store = storeRef.current;

  const state = useSyncExternalStore(
    store.subscribe,
    store.getState,
    store.getState
  );

  const update = useCallback(
    (partial: Partial<ScheduleSharedState>) => store.setState(partial),
    [store]
  );

  return { state, update, getState: store.getState };
}
